import React, { useState, useEffect, forwardRef } from "react";
import { Stripe } from "@stripe/stripe-js";

import getLogger, { LogGroup } from "js/core/logger";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { getStripe, STRIPE_LOAD_ERROR } from "js/core/services/stripe";

const logger = getLogger(LogGroup.STRIPE);

type InjectedProps = { stripe: Stripe };
type WrapperProps<T extends React.ComponentType<any>> = Omit<React.ComponentProps<T>, keyof InjectedProps> & Partial<InjectedProps>;

export default function withStripe<T extends React.ComponentType<any>>(Component: T) {
    return forwardRef<T, WrapperProps<T>>(function ComponentWithStripe(props, componentRef) {
        if (props.stripe) {
            // Already passed
            // @ts-ignore
            return <Component {...props} ref={componentRef} />;
        }

        const [stripe, setStripe] = useState<Stripe | null>(null);

        useEffect(() => {
            let isMounted = true;

            getStripe()
                .then(stripe => {
                    if (!isMounted) {
                        return;
                    }

                    setStripe(stripe);
                })
                .catch(err => {
                    logger.warn(err, "withStripe() failed to load Stripe.js");
                    // This is an HOC and we need to close the component
                    // if we have an issue loading Stripe.js
                    props.closeDialog?.();
                    ShowErrorDialog({
                        title: "Unable to load secure payment form",
                        message: STRIPE_LOAD_ERROR
                    });
                });

            return () => {
                isMounted = false;
            };
        }, []);

        if (!stripe) {
            return null;
        }

        // @ts-ignore
        return <Component {...props} stripe={stripe} ref={componentRef} />;
    });
}
